import { useEffect, useState } from 'react';
import { parseSavedSearch, referenceKey, searchReference } from './searchModel';
import type { SearchItem, SearchReference } from './searchModel';
import { useUserStore } from '@/entities/user/store';

const empty = () => ({
  recent: [] as Array<SearchReference>,
  favorites: [] as Array<SearchReference>,
});
/** Local to the signed-in user; no query text, client name, profile or photograph is persisted. */
export function useSearchLibrary() {
  const userId = useUserStore((s) => s.user?.id);
  const key = userId ? `biasafe-search-v1:${userId}` : null;
  const [state, setState] = useState<{
    key: string | null;
    data: ReturnType<typeof empty>;
  }>({ key: null, data: empty() });
  useEffect(() => {
    let data = empty();
    try {
      if (key) data = parseSavedSearch(localStorage.getItem(key));
    } catch {
      /* Storage can be disabled. */
    }
    setState({ key, data });
  }, [key]);
  const data = state.key === key ? state.data : empty();
  // Persist committed state, never inside React's replayable state updater.
  // A user switch must finish loading its own library before anything is written.
  useEffect(() => {
    if (!key || state.key !== key) return;
    try {
      localStorage.setItem(key, JSON.stringify(state.data));
    } catch {
      /* Search remains usable without persistence. */
    }
  }, [key, state]);
  function update(
    fn: (old: ReturnType<typeof empty>) => ReturnType<typeof empty>,
  ) {
    if (!key) return;
    setState((previous) => {
      const next = fn(previous.key === key ? previous.data : empty());
      return { key, data: next };
    });
  }
  return {
    ...data,
    enabled: !!key,
    remember: (item: SearchItem) =>
      update((old) => {
        const ref = searchReference(item);
        return {
          ...old,
          recent: [
            ref,
            ...old.recent.filter((r) => referenceKey(r) !== referenceKey(ref)),
          ].slice(0, 8),
        };
      }),
    toggle: (item: SearchItem) =>
      update((old) => {
        const ref = searchReference(item),
          found = old.favorites.some(
            (r) => referenceKey(r) === referenceKey(ref),
          );
        return {
          ...old,
          favorites: found
            ? old.favorites.filter((r) => referenceKey(r) !== referenceKey(ref))
            : [...old.favorites, ref].slice(-12),
        };
      }),
    clear: () => update((old) => ({ ...old, recent: [] })),
  };
}
